import React from 'react';
import { cn } from '../../utils/cn';

// One colour per tone. Pages map their own statuses to a tone (e.g. 'Resolved' -> 'emerald'),
// and the badge look (tint, ring, dark mode) lives here.
const BADGE = {
    slate: 'bg-slate-100 text-slate-600 ring-slate-500/20 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-500/30',
    brand: 'bg-brand-50 text-brand-700 ring-brand-600/20 dark:bg-brand-500/10 dark:text-brand-300 dark:ring-brand-400/25',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20',
    amber: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20',
    rose: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
    violet: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/20',
    orange: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-400/20',
    pink: 'bg-pink-50 text-pink-700 ring-pink-600/20 dark:bg-pink-500/10 dark:text-pink-300 dark:ring-pink-400/20',
    cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-400/20',
    teal: 'bg-teal-50 text-teal-700 ring-teal-600/20 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/20',
};

const DOT = {
    slate: 'bg-slate-400', brand: 'bg-brand-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500',
    violet: 'bg-violet-500', orange: 'bg-orange-500', pink: 'bg-pink-500', cyan: 'bg-cyan-500', teal: 'bg-teal-500',
};

// Meaning-based names used by the dashboard and transaction statuses
const SEMANTIC = { success: 'emerald', pending: 'amber', failed: 'rose', info: 'brand', neutral: 'slate' };
const resolve = (tone) => SEMANTIC[tone] || (BADGE[tone] ? tone : 'slate');

// Transaction status codes used by the API: 1 success, 0/3 pending, 2 failed
const STATUS_TONE = { 1: 'success', 0: 'pending', 3: 'pending', 2: 'failed' };
export const toneForStatus = (status) => STATUS_TONE[parseInt(status, 10)] || 'neutral';

export const StatusDot = ({ tone = 'neutral', className }) => (
    <span aria-hidden="true" className={cn('inline-block h-2 w-2 shrink-0 rounded-full', DOT[resolve(tone)], className)} />
);

export function Badge({ tone = 'slate', dot = false, className, children, ...props }) {
    const color = resolve(tone);
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                BADGE[color],
                className
            )}
            {...props}
        >
            {dot && <StatusDot tone={color} className="h-1.5 w-1.5" />}
            {children}
        </span>
    );
}

// Badge with a status dot, for success / pending / failed style states
function StatusBadge({ tone = 'neutral', dot = true, ...props }) {
    return <Badge tone={tone} dot={dot} {...props} />;
}

export default StatusBadge;
